import { useState } from 'react';
import { injectIntl } from 'react-intl';
import { Form, Input } from 'antd';
import { withRouter } from 'react-router-dom';

import { emailRule, requiredRule } from 'utils/forms';
import { forgetPassword, login } from 'services/Security';
import { PASSWORD_EXPIRED_ERROR_CODE } from 'constants/Security';
import { openModal } from 'utils/modal';
import { GOOGLE_SSO_LINKS } from 'constants/signin';

import LoadingWrapper from 'components/LoadingWrapper/LoadingWrapper';
import SignPage from 'containers/SignPage/index';
import DomainSelectorModal from './components/DomainSelectorModal/DomainSelectorModal';
import BRButton from 'components/BRButton/BRButton';
import { notify } from 'components/Notify/Notify';

import GoogleLogo from 'assets/google-logo.png';
import { ReactComponent as BostaIcon } from 'assets/imgRevamp/BostaLogo.svg';

import './SignIn.less';

const SignIn = ({ intl, history }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleSignin = async (values) => {
    setIsLoading(true);
    try {
      const data = await login(values);
      if (data?.data?.twoFAType) {
        history.push({
          pathname: '/verify-user',
          state: {
            userEmailPassword: values,
            response: data
          }
        });
      } else {
        const { token, user, refreshToken } = data;

        if (
          user?.roles?.length === 1 &&
          user?.roles?.includes('BUSINESS_ADMIN')
        ) {
          history.push('/401');
          return;
        }

        if (user.country) {
          localStorage.setItem(
            'userAutoCodCollection',
            JSON.stringify(user.country)
          );
        } else {
          localStorage.setItem('userAutoCodCollection', null);
        }

        localStorage.setItem('userInfo', JSON.stringify(user));
        localStorage.setItem('token', token);
        localStorage.setItem('refreshToken', refreshToken);
        window.location.reload();
      }
    } catch (error) {
      if (error?.errorCode === PASSWORD_EXPIRED_ERROR_CODE) {
        await forgetPassword({ email: values.email, source: '2' });
        notify(
          intl.formatMessage(
            { id: 'sign_in.password_expired' },
            {
              email: values.email
            }
          ),
          'success'
        );
      } else {
        notify(error.message);
      }
    }
    setIsLoading(false);
  };

  const handleSSOSignIn = (value) => {
    window.location.href = GOOGLE_SSO_LINKS[value];
  };

  const handleSignInWithGoogleClick = () => {
    openModal(DomainSelectorModal, { onCardClick: handleSSOSignIn });
  };

  return (
    <SignPage
      content={
        <LoadingWrapper loading={isLoading}>
          <div className="br-sign-in__container">
            <div className="br-sign-in__card">
              <BostaIcon />
              <div className="br-sign-in__title display-md">
                {intl.formatMessage({ id: 'sign_in.title' })}
              </div>
              <Form onFinish={handleSignin}>
                <Form.Item
                  name="email"
                  label={intl.formatMessage({ id: 'sign_in.labels.email' })}
                  rules={[
                    requiredRule(
                      intl.formatMessage(
                        {
                          id: 'form.required_custom'
                        },
                        {
                          label: intl.formatMessage({
                            id: 'sign_in.labels.email'
                          })
                        }
                      )
                    ),
                    emailRule(
                      intl.formatMessage({
                        id: 'form.email_not_valid'
                      })
                    )
                  ]}
                >
                  <Input
                    placeholder={intl.formatMessage({
                      id: 'sign_in.placeholder.email'
                    })}
                  />
                </Form.Item>
                <Form.Item
                  name="password"
                  className="br-sign-in-password__form-item"
                  label={
                    <div className="br-sign-in-forgot-password__button">
                      {intl.formatMessage({ id: 'sign_in.labels.password' })}
                      <BRButton
                        type="link-color"
                        onClick={() => history.push('/forget-password')}
                        label={intl.formatMessage({
                          id: 'sign_in.labels.forgot_password'
                        })}
                        tabindex="-1"
                      />
                    </div>
                  }
                  rules={[
                    requiredRule(
                      intl.formatMessage(
                        {
                          id: 'form.required_custom'
                        },
                        {
                          label: intl.formatMessage({
                            id: 'sign_in.labels.password'
                          })
                        }
                      )
                    )
                  ]}
                >
                  <Input.Password />
                </Form.Item>
                <div className="br-sign-form__actions">
                  <BRButton
                    type="primary"
                    htmlType="submit"
                    disabled={isLoading}
                    label={intl.formatMessage({ id: 'sign_in.confirm_action' })}
                  />
                  <BRButton
                    label={intl.formatMessage({
                      id: 'sign_in.sign_in_with'
                    })}
                    onClick={handleSignInWithGoogleClick}
                    suffixIcon={
                      <img
                        className="br-sign-in-google__icon"
                        src={GoogleLogo}
                        alt="google-logo"
                      />
                    }
                  />
                </div>
              </Form>
            </div>
          </div>
        </LoadingWrapper>
      }
    />
  );
};

export default withRouter(injectIntl(SignIn));
